// @ts-nocheck
import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus,
  Download,
  MoreHorizontal,
  FileText,
  Truck,
  Calendar,
  Edit,
  ChevronDown,
  ChevronRight,
  PackageCheck,
  CheckCircle2,
  Upload,
  FileDown,
} from "lucide-react";
import { useCustomerLookup } from "@/hooks/useFKLookup";
import FQCReadySelectModal from "./components/FQCReadySelectModal";
import { ReadyToDispatchTab } from "./components/ReadyToDispatchTab";
import {
  canUpdateDeliveryLocation,
  canEditDeliveryDocument,
} from "./DeliveryForm";

export { canUpdateDeliveryLocation, canEditDeliveryDocument };
import {
  CompactKpiRibbon,
  type CompactKpiItem,
} from "@/components/shared/CompactKpiRibbon";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, formatMoney } from "@/lib/utils";
import {
  downloadExcelTemplate as downloadTemplate,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { useExcelExport } from "@/hooks/useExcelExport";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { ImportDialog } from "@/components/shared/ImportDialog";
import { ResponsiveDataView } from "@/components/shared/ResponsiveDataView";

import { BulkActionBar, BulkAction } from "@/components/shared/BulkActionBar";
import { Printer, Trash2, CheckSquare, AlertCircle } from "lucide-react";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { trpcClient } from "@/lib/trpc";
import type { Delivery } from "@/shared/schemas";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { getStatusOptions } from "@/config/status-registry";
import {
  DateGroupedTable,
  DateGroupToggleButton,
  extractDateKey,
} from "@/components/shared/DateGroupedTable";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { SortableHeader } from "@/components/shared/SortableHeader";
import {
  useDatePeriodFilter,
  DatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import { type FilterField } from "@/components/shared/FilterBar";
import PrintDialog from "@/components/shared/PrintDialog";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";


import { DeliveryStatus } from "@/config/status-registry";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "deliveryId", label: "Mã PX" },
  { key: "customerName", label: "Khách hàng" },
  {
    key: "deliveryDate",
    label: "Ngày giao",
    type: "date",
    format: (val: any) =>
      val ? new Date(val).toLocaleDateString("vi-VN") : "",
  },
  { key: "status", label: "Trạng thái" },
  { key: "vehicleNumber", label: "Biển số xe" },
  { key: "driverName", label: "Tài xế" },
  // Details
  { key: "productCode", label: "Mã SP" },
  { key: "productName", label: "Tên SP" },
  { key: "poNumber", label: "PO" },
  { key: "soNumber", label: "SO" },
  { key: "unit", label: "ĐVT" },
  {
    key: "quantity",
    label: "SL Đặt",
    type: "number",
  },
  {
    key: "deliveredQty",
    label: "Thực giao",
    type: "number",
  },
  {
    key: "lossQty",
    label: "Bù hao",
    type: "number",
  },
  {
    key: "countedQty",
    label: "Thực tính",
    type: "number",
  },
  {
    key: "unitPrice",
    label: "Đơn giá",
    type: "decimal",
  },
  {
    key: "amount",
    label: "Thành tiền",
    type: "decimal",
    format: (_: any, row: any) =>
      row?.amount || ((row?.countedQty || row?.deliveredQty || 0) * (row?.unitPrice || 0)),
  },
  { key: "notes", label: "Ghi chú" },
];

// Build status items from registry (exclude legacy capitalized keys)
import { useRowSelection } from "@/hooks/useRowSelection";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";
import { emsTabClass } from "@/components/ui/ems-tabs";

const STATUS_ITEMS: StatusSidebarItem[] = Object.entries(DeliveryStatus)
  .filter(([key]) => key === key.toUpperCase())
  .map(([key, cfg]) => ({
    key,
    label: cfg.label,
    color: cfg.color,
    dotColor: cfg.dotColor,
  }));

export default function DeliveryListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { options: customerOptions } = useCustomerLookup();
  const { hasPermission } = useRoleAccess();
  const [showImport, setShowImport] = useState(false);
  const [printDelivery, setPrintDelivery] = useState<any>(null);
  const [showFqcModal, setShowFqcModal] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedChildIds, setSelectedChildIds] = useState<Set<string>>(
    new Set(),
  );
  const routerSearch = (useRouterState({ select: (s) => s.location.search }) as Record<string, any>) || {};
  const [activeMainTab, setActiveMainTab] = useState<"ready_to_dispatch" | "delivery_notes">(
    routerSearch?.tab === "ready_to_dispatch" ? "ready_to_dispatch" : "delivery_notes"
  );
  const [isGrouped, setIsGrouped] = useState(true);

  const handleMainTabChange = (tab: "ready_to_dispatch" | "delivery_notes") => {
    setActiveMainTab(tab);
    navigate({
      search: (prev: any) => ({
        ...prev,
        tab: tab === "ready_to_dispatch" ? "ready_to_dispatch" : undefined,
      }),
      replace: true,
    });
  };

  const { data: tabCounts, refetch: refetchTabCounts } = useQuery({
    queryKey: ["delivery-tab-counts"],
    queryFn: async () => trpcClient.deliveries.getTabCounts.query({}),
  });

  const filterFields = useMemo<FilterField[]>(() => [
    { key: "deliveryId", label: "Mã phiếu", type: "text", placeholder: "PX-..." },
    {
      key: "customerId",
      label: "Khách hàng",
      type: "combobox-multi",
      options: customerOptions.map((opt) => ({ value: opt.id, label: opt.label })),
    },
    {
      key: "productName",
      label: "Sản phẩm",
      type: "text",
      placeholder: "Tên SP...",
    },
    {
      key: "deliveryType",
      label: "Loại hình",
      type: "combobox-multi",
      options: [
        { value: "NORMAL", label: "Giao thường" },
        { value: "EXPRESS", label: "Giao gấp" },
        { value: "SAMPLE", label: "Giao mẫu" },
      ],
    },
    {
      key: "status",
      label: "Trạng thái",
      type: "combobox-multi",
      options: getStatusOptions("DeliveryStatus", false).filter(o =>
        ["DRAFT", "SCHEDULED", "IN_TRANSIT", "DELIVERED", "CANCELLED"].includes(o.value)
      ),
    },
  ], [customerOptions]);

  const { data: fqcData } = useQuery({
    queryKey: ["fqc-ready-batches"],
    queryFn: async () => trpcClient.deliveries.getFqcReadyBatches.query({}),
  });
  const fqcReadyCount = fqcData?.total || 0;

  const toggleSelectChild = (id: string) => {
    setSelectedChildIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllChild = (childIds: string[]) => {
    setSelectedChildIds((prev) => {
      const next = new Set(prev);
      const allSelected = childIds.every((id) => next.has(id));
      if (allSelected) {
        childIds.forEach((id) => next.delete(id));
      } else {
        childIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirm = useConfirm();
  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter();

  const {
    items: allDeliveries = [],
    paginatedItems = [],
    filtered = [],
    error = null,
    searchQuery,
    filterValues,
    serverParams,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    statusCounts: serverStatusCounts,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
    refetch,
  } = useServerListFilter<any>({
    queryKey: "deliveries",
    queryFn: (params) => trpcClient.deliveries.list.query(params) as any,
    extraParams: dateParams.from
      ? { deliveryDateFrom: dateParams.from, deliveryDateTo: dateParams.to }
      : {},
  });

  const rowIds = paginatedItems.map((d: Delivery) => d.deliveryId);
  const rowSelection = useRowSelection(rowIds);
  const {
    selectedIds,
    selectionMode,
    clearSelection,
    selectedCount,
    toggleSelect,
    toggleSelectAll,
    allSelected,
    handleRowSelect,
    wasLongPress,
  } = rowSelection;

  const flattenDeliveriesForExport = useCallback((deliveriesList: any[]) => {
    const data: any[] = [];
    (deliveriesList || []).forEach((delivery: any) => {
      const customerName = delivery.customer?.name || delivery.customer?.fullName || delivery.customer?.shortName || "";
      if (delivery.deliveryItems && delivery.deliveryItems.length > 0) {
        delivery.deliveryItems.forEach((detail: any) => {
          data.push({
            ...delivery,
            id: delivery.deliveryId || delivery.id,
            deliveryId: delivery.deliveryId || delivery.id,
            customerName,
            productCode: detail.product?.productCode || detail.productCode || "",
            productName: detail.product?.productName || detail.productName || "",
            poNumber: detail.poNumber || detail.poReference || "",
            soNumber: detail.soNumber || detail.soReference || "",
            unit: detail.unit || "",
            quantity: detail.quantity ?? detail.orderedQty ?? 0,
            deliveredQty: detail.deliveredQty ?? detail.actualQty ?? 0,
            lossQty: detail.lossQty ?? 0,
            countedQty: detail.countedQty ?? detail.deliveredQty ?? 0,
            unitPrice: detail.unitPrice || detail.priceVND || 0,
            amount: detail.amount || ((detail.countedQty || detail.deliveredQty || 0) * (detail.unitPrice || 0)),
            notes: detail.notes || "",
          });
        });
      } else {
        data.push({
          ...delivery,
          id: delivery.deliveryId || delivery.id,
          deliveryId: delivery.deliveryId || delivery.id,
          customerName,
        });
      }
    });
    return data;
  }, []);

  const { handleExport, isExporting } = useExcelExport({
    filename: "phieu_giao_hang_chi_tiet",
    sheetName: "Phiếu Giao Hàng",
    columns: EXPORT_COLUMNS,
    queryFn: (params) => trpcClient.deliveries.list.query(params),
    currentFilters: serverParams,
    transformData: flattenDeliveriesForExport,
    selectedIds,
    idKey: "deliveryId",
  });

  const handleImport = async (rows: Record<string, unknown>[]) => {
    let success = 0;
    const errors: string[] = [];

    // Group by deliveryId
    const grouped: Record<string, any> = {};
    for (const row of rows) {
      const dId = row.deliveryId as string;
      if (!dId) continue;
      if (!grouped[dId]) {
        grouped[dId] = {
          deliveryId: dId,
          status: row.status,
          deliveryItems: [],
        };
      }
      if (row.productCode || row.productName) {
        grouped[dId].deliveryItems.push({
          productCode: row.productCode,
          productName: row.productName,
          poReference: row.poReference,
          soReference: row.soReference,
          orderedQty: Number(row.orderedQty) || 0,
          plannedQty: Number(row.plannedQty) || 0,
          actualQty: Number(row.actualQty) || 0,
          unitPrice: Number(row.unitPrice) || 0,
          notes: row.notes || "",
        });
      }
    }

    const toastId = toast.loading(
      `Đang xử lý ${Object.keys(grouped).length} phiếu...`,
    );
    for (const [id, data] of Object.entries(grouped)) {
      try {
        // Nhập Excel chỉ đổi trạng thái (qua quy trình trừ / hoàn kho). Dòng hàng gắn dòng đơn và lô đóng gói nên sửa trên phiếu giao
        await trpcClient.deliveries.update.mutate({
          id,
          data: { status: data.status },
        });
        success++;
      } catch (err: any) {
        errors.push(`${id}: ${err?.message || "Lỗi"}`);
      }
    }

    if (success > 0) {
      const hadLines = Object.values(grouped).some((g: any) => g.deliveryItems.length > 0);
      toast.success(
        `Đã cập nhật trạng thái ${success} phiếu giao` + (hadLines ? ". Dòng hàng trong file không được nhập; sửa dòng hàng trên từng phiếu giao." : ""),
        { id: toastId },
      );
      await queryClient.invalidateQueries({ queryKey: ["deliveries"] });
    } else {
      toast.dismiss(toastId);
    }
    if (errors.length > 0)
      toast.error(
        `${errors.length} dòng lỗi: ${errors.slice(0, 3).join("; ")}`,
      );
  };

  const getBulkActions = (): BulkAction[] => {
    const selectedItems = allDeliveries.filter((i: any) =>
      selectedIds.has(i.deliveryId),
    );
    if (selectedItems.length === 0) return [];
    // Quy trình cho Đã giao từ Đã xếp lịch / Đang giao (trạng thái cũ "Processing", "SHIPPED" không tồn tại nên nút luôn hỏng)
    const allShippable = selectedItems.every((i: any) =>
      ["SCHEDULED", "IN_TRANSIT"].includes(String(i.status).toUpperCase()),
    );

    return [
      {
        label: "Đã giao",
        icon: <CheckSquare className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) => trpcClient.deliveries.updateStatus.mutate({ id: String(id), status: "DELIVERED" }),
            actionName: "xác nhận đã giao",
            itemType: "phiếu giao hàng",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["deliveries"] });
            },
            requireConfirm: true,
            confirmMessage: `Xác nhận đã giao ${selectedCount} phiếu giao hàng đã chọn?`,
          });
        },
        disabled: !allShippable,
      },
      {
        label: "In Vận đơn",
        icon: <Printer className="w-3.5 h-3.5" />,
        onClick: async () => {
          const toastId = toast.loading("Đang tải dữ liệu để in...");
          try {
            const ids = Array.from(selectedIds);
            const data = await Promise.all(
              ids.map((id) => trpcClient.deliveries.getById.query({ id })),
            );
            toast.dismiss(toastId);
            setPrintDelivery(data);
          } catch (err: any) {
            toast.error(
              `Lỗi khi tải dữ liệu: ${err?.message || "Lỗi không xác định"}`,
              { id: toastId },
            );
          }
        },
      },
      {
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) => trpcClient.deliveries.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "phiếu giao hàng",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["deliveries"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} phiếu giao hàng đã chọn?`,
          });
        },
        variant: "destructive",
      },
    ];
  };

  const filterTags = React.useMemo(() => {
    return STATUS_ITEMS.map((item) => {
      const sc = serverStatusCounts?.find(
        (s: any) =>
          s.status?.toString()?.toUpperCase() === item.key?.toUpperCase(),
      );
      return {
        ...item,
        count: sc ? sc.count : 0,
      };
    });
  }, [serverStatusCounts]);

  const inTransitCount =
    serverStatusCounts?.find((s: any) => s.status === "IN_TRANSIT")?.count || 0;
  const deliveredCount =
    (serverStatusCounts?.find((s: any) => s.status === "DELIVERED")?.count ||
      0) +
    (serverStatusCounts?.find((s: any) => s.status === "COMPLETED")?.count ||
      0);

  const deliveryKpiItems = useMemo<CompactKpiItem[]>(() => [
    {
      key: "ALL",
      label: "Tổng Phiếu Giao",
      count: totalItems,
      icon: FileText,
      variant: "default",
      tooltip: "Chứng từ xuất giao",
    },
    {
      key: "FQC_READY",
      label: "Lô FQC Sẵn Sàng Xếp Xe",
      count: fqcReadyCount,
      icon: PackageCheck,
      variant: "emerald",
      tooltip: "Bấm để mở danh sách lô FQC và tạo phiếu XK",
      animate: fqcReadyCount > 0,
      onClick: () => setShowFqcModal(true),
    },
    {
      key: "IN_TRANSIT",
      label: "Đang Vận Chuyển",
      count: inTransitCount,
      icon: Truck,
      variant: "indigo",
      tooltip: "Xe đang trên đường",
    },
    {
      key: "DELIVERED",
      label: "Đã Giao Hoàn Tất",
      count: deliveredCount,
      icon: CheckCircle2,
      variant: "blue",
      tooltip: "Đã bàn giao khách hàng",
    },
  ], [totalItems, fqcReadyCount, inTransitCount, deliveredCount]);

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Lỗi tải dữ liệu</h3>
        <p className="text-slate-500 mb-4">
          Không thể tải danh sách phiếu xuất kho
        </p>
        <Button onClick={() => window.location.reload()}>Thử lại</Button>
      </div>
    );
  }

  const activeKpiKey = statusFilter
    ? Array.isArray(statusFilter)
      ? statusFilter[0]
      : statusFilter
    : "ALL";

  const handleSelectKpi = (key: string | null) => {
    if (!key || key === "ALL") {
      onStatusChange("");
    } else {
      onStatusChange(statusFilter === key ? "" : key);
    }
  };

  return (
    <>
      <ListPageShell
        trashModule="deliveries"
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => handleMainTabChange("delivery_notes")}
              className={emsTabClass(activeMainTab === "delivery_notes", { size: "sm" })}
            >
              <Truck className="w-3.5 h-3.5" />
              <span>Danh Sách Phiếu Giao</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold",
                  activeMainTab === "delivery_notes"
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-100 text-slate-500"
                )}
              >
                {tabCounts?.all ?? totalItems}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleMainTabChange("ready_to_dispatch")}
              className={emsTabClass(activeMainTab === "ready_to_dispatch", { size: "sm" })}
            >
              <PackageCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span>Chờ Xuất Giao</span>
              <span
                className={cn(
                  "px-1.5 py-0.2 rounded-full text-[10px] font-bold",
                  activeMainTab === "ready_to_dispatch"
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-slate-100 text-slate-500"
                )}
              >
                {tabCounts?.readyToDispatch ?? fqcReadyCount}
              </span>
            </button>
          </div>
        }
        headerFilters={
          activeMainTab === "delivery_notes" ? (
            <div className="flex items-center gap-1.5 flex-nowrap shrink-0">
              <DateGroupToggleButton
                isGrouped={isGrouped}
                onToggle={() => setIsGrouped(!isGrouped)}
              />
              <PageFilterTags
                items={filterTags}
                selectedKeys={
                  statusFilter
                    ? Array.isArray(statusFilter)
                      ? statusFilter
                      : [statusFilter]
                    : []
                }
                onSelect={onStatusChange}
              />
            </div>
          ) : undefined
        }
        dateFilter={
          activeMainTab === "delivery_notes" ? (
            <DatePeriodFilter
              period={period}
              setPeriod={setPeriod}
              customFrom={customFrom}
              setCustomFrom={setCustomFrom}
              customTo={customTo}
              setCustomTo={setCustomTo}
            />
          ) : undefined
        }
        unitLabel={activeMainTab === "ready_to_dispatch" ? "Mặt hàng" : "Phiếu"}
        onExport={handleExport}
        exportLoading={isExporting}
        secondaryActions={
          hasPermission("deliveries", "create")
            ? [
                {
                  key: "import-deliveries",
                  label: "Nhập Excel",
                  icon: <Upload className="w-3.5 h-3.5 text-emerald-700" />,
                  onClick: () => setShowImport(true),
                },
              ]
            : undefined
        }
        actions={
          <div
            data-slot="mobile-actions"
            className="flex items-center gap-1.5 flex-nowrap shrink-0"
          >
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowFqcModal(true)}
              className="h-7 px-2.5 text-xs gap-1 border-emerald-300 text-emerald-700 bg-emerald-50/50 hover:bg-emerald-100 hover:text-emerald-800 font-medium shrink-0"
            >
              <PackageCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span>Giao FQC</span>
            </Button>
            {hasPermission("deliveries", "create") && (
              <Button
                size="sm"
                onClick={() => navigate({ to: "/delivery/create" })}
                className="h-7 px-2.5 text-xs gap-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shrink-0"
              >
                <Plus className="w-3.5 h-3.5 shrink-0" />
                <span>Lập phiếu</span>
              </Button>
            )}
          </div>
        }
        filterFields={filterFields}
        filterValues={filterValues}
        showFilter={activeMainTab === "delivery_notes" && showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        currentPage={activeMainTab === "ready_to_dispatch" ? 1 : currentPage}
        pageSize={activeMainTab === "ready_to_dispatch" ? Math.max(100, (tabCounts?.readyToDispatch || 100)) : pageSize}
        totalItems={activeMainTab === "ready_to_dispatch" ? (tabCounts?.readyToDispatch || 0) : totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        {activeMainTab === "ready_to_dispatch" ? (
          <ReadyToDispatchTab />
        ) : (
          <ResponsiveDataView
          items={paginatedItems}
          emptyState={
            searchQuery
              ? `Không tìm thấy phiếu "${searchQuery}"`
              : "Không có dữ liệu"
          }
          renderMobileCard={(item: any) => {
            const getStatusBg = (st: string) => {
              switch (st) {
                case "DRAFT":
                  return "border-slate-200";
                case "CONFIRMED":
                  return "border-blue-200";
                case "IN_TRANSIT":
                  return "border-indigo-200";
                case "DELIVERED":
                  return "border-emerald-200";
                case "CANCELLED":
                  return "border-slate-200";
                default:
                  return "border-slate-200";
              }
            };
            return (
              <div
                className={cn(
                  "bg-white rounded-xl shadow-sm border overflow-hidden active:scale-[0.99] transition-transform flex flex-col",
                  selectedIds.has(item.deliveryId)
                    ? "border-indigo-400 ring-1 ring-indigo-400"
                    : getStatusBg(item.status),
                )}
                onClick={(e) => {
                  if (selectionMode) {
                    handleRowSelect(item.deliveryId, e);
                    return;
                  }
                  navigate({
                    to: "/delivery/$deliveryId",
                    params: { deliveryId: item.deliveryId },
                  });
                }}
              >
                <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex items-center gap-2">
                    {selectionMode && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(item.deliveryId)}
                          onCheckedChange={() => toggleSelect(item.deliveryId)}
                        />
                      </div>
                    )}
                    <span className="font-bold text-slate-800 text-sm">
                      {item.deliveryId}
                    </span>
                  </div>
                  <StatusBadge
                    type="DeliveryStatus"
                    value={item.status}
                    className="text-[10px] px-1.5 py-0 min-h-0 h-5"
                  />
                </div>

                <div className="p-3 flex flex-col gap-3">
                  <div className="flex flex-col">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">
                      Khách hàng
                    </span>
                    <span className="text-sm font-semibold text-slate-900 leading-tight">
                      {item.customer?.shortName || item.customer?.name || "—"}
                    </span>
                  </div>

                  <div className="flex items-start justify-between border-t border-slate-100 border-dashed pt-3">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-1.5 text-xs text-slate-600">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        <span>
                          {item.deliveryDate
                            ? new Date(item.deliveryDate).toLocaleDateString(
                                "vi-VN",
                              )
                            : "—"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-600">
                        <Truck className="w-3.5 h-3.5 text-slate-400" />
                        <span>{item.vehicleNumber || "—"}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">
                        Giá trị
                      </span>
                      <span className="font-mono font-bold text-sm text-slate-900">
                        {formatMoney(
                          item.totalAmount ||
                            item.deliveryItems?.reduce(
                              (s: number, d: any) =>
                                s +
                                (d.deliveredQty || d.quantity || 0) *
                                  (d.unitPrice || d.priceVND || 0),
                              0,
                            ) ||
                            0,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          }}
        >
          <div className="overflow-x-auto apv-compact-table h-full erp-table-scroll">
            <Table
              className={cn(
                "table-fixed w-full min-w-[860px]",
                selectionMode && "apv-select-mode",
              )}
            >
              <TableHeader className="apv-table-header">
                <TableRow>
                  <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                  <SortableHeader
                    label="Mã phiếu"
                    field="deliveryId"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="apv-col-code w-[195px]"
                  />
                  <SortableHeader
                    label="Khách hàng & Thời gian"
                    field="customer.shortName"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="min-w-[240px]"
                  />
                  <SortableHeader
                    label="Tài xế & Xe"
                    field="driverName"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="w-[210px]"
                  />
                  <SortableHeader
                    label="Giá trị"
                    field="totalAmount"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="w-[140px] text-right"
                  />
                  <TableHead className="w-[80px] text-right apv-col-actions"></TableHead>
                </TableRow>
                {showFilter && (
                  <InlineFilterRow
                    filterFields={filterFields}
                    filterValues={filterValues}
                    onFilterChange={onFilterChange}
                    columns={[
                      selectionMode ? CHECKBOX_COL_NULL : null,
                      { key: "deliveryId" },
                      { key: "customerId" },
                      null,
                      null,
                      null,
                    ]}
                  />
                )}
              </TableHeader>
              <TableBody>
                <DateGroupedTable<any>
                  items={paginatedItems}
                  isGrouped={isGrouped}
                  getDateKey={(item) => extractDateKey(item.deliveryDate || item.createdAt)}
                  renderGroupHeader={(group) => {
                    const groupDeliveredUnits = group.items.reduce(
                      (s, item) =>
                        s +
                        (item.deliveryItems?.reduce(
                          (acc: number, di: any) =>
                            acc + (di.deliveredQty || di.quantity || 0),
                          0,
                        ) || 0),
                      0,
                    );
                    const groupTotalAmount = group.items.reduce(
                      (s, item) =>
                        s +
                        (Number(item.totalAmount) ||
                          item.deliveryItems?.reduce(
                            (acc: number, di: any) =>
                              acc +
                              (di.deliveredQty || di.quantity || 0) *
                                (di.unitPrice || di.priceVND || 0),
                            0,
                          ) ||
                          0),
                      0,
                    );

                    return (
                      <TableRow
                        key={`group-${group.dateKey}`}
                        className={cn(
                          "border-y font-semibold text-xs transition-colors",
                          group.isToday
                            ? "bg-[#eef2ff] hover:bg-[#e0e7ff] border-indigo-200"
                            : "bg-[#f1f5f9] hover:bg-[#e2e8f0] border-slate-300",
                        )}
                      >
                        <TableCell colSpan={4} className="py-2 px-3 text-slate-800">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 p-0 text-slate-600 hover:text-slate-900 shrink-0"
                              onClick={group.toggleCollapse}
                              title={group.isCollapsed ? "Bung ra" : "Thu gọn"}
                            >
                              {group.isCollapsed ? (
                                <ChevronRight size={15} />
                              ) : (
                                <ChevronDown size={15} />
                              )}
                            </Button>
                            <span className="font-bold text-slate-800 text-[13px] flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                              {group.formattedDate}
                            </span>
                            <Badge
                              variant="outline"
                              className="bg-white/80 text-slate-700 border-slate-300 text-[11px] font-bold px-2 py-0"
                            >
                              {group.count} phiếu giao
                            </Badge>
                            {groupDeliveredUnits > 0 && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] bg-slate-200 text-slate-700 font-bold px-1.5 py-0"
                              >
                                {groupDeliveredUnits.toLocaleString("vi-VN")} sp
                              </Badge>
                            )}
                            {group.isToday && (
                              <Badge className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0 border-0">
                                HÔM NAY
                              </Badge>
                            )}
                            {group.isCollapsed && (
                              <span className="text-[11px] text-slate-500 font-normal italic">
                                (Đang thu gọn - bấm để xem chi tiết)
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-2 px-3 text-right font-mono font-bold text-slate-900">
                          {formatMoney(groupTotalAmount)}
                        </TableCell>
                        <TableCell className="py-2 px-2"></TableCell>
                      </TableRow>
                    );
                  }}
                  renderPageSummary={(items) => {
                    const pageTotalAmount = items.reduce(
                      (s, item) =>
                        s +
                        (Number(item.totalAmount) ||
                          item.deliveryItems?.reduce(
                            (acc: number, di: any) =>
                              acc +
                              (di.deliveredQty || di.quantity || 0) *
                                (di.unitPrice || di.priceVND || 0),
                            0,
                          ) ||
                          0),
                      0,
                    );
                    const pageDeliveredUnits = items.reduce(
                      (s, item) =>
                        s +
                        (item.deliveryItems?.reduce(
                          (acc: number, di: any) =>
                            acc + (di.deliveredQty || di.quantity || 0),
                          0,
                        ) || 0),
                      0,
                    );

                    return (
                      <TableRow className="bg-slate-100 font-bold border-t-2 border-slate-300 text-xs">
                        <TableCell
                          colSpan={4}
                          className="py-2.5 px-3 text-slate-800 text-right uppercase tracking-wider font-semibold"
                        >
                          Tổng cộng trang ({items.length} phiếu{pageDeliveredUnits > 0 ? ` • ${pageDeliveredUnits.toLocaleString("vi-VN")} sp` : ""}):
                        </TableCell>
                        <TableCell className="py-2.5 px-3 text-right font-mono text-slate-900 font-black text-[13px]">
                          {formatMoney(pageTotalAmount)}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    );
                  }}
                  emptyState={
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-6 text-sm text-muted-foreground"
                      >
                        Không có dữ liệu
                      </TableCell>
                    </TableRow>
                  }
                  renderRow={(item: any) => {
                    const getStatusBg = (st: string) => {
                      switch (st) {
                        case "DRAFT":
                          return "bg-slate-50/50";
                        case "CONFIRMED":
                          return "bg-blue-50/30";
                        case "IN_TRANSIT":
                          return "bg-indigo-50/30";
                        case "DELIVERED":
                          return "bg-emerald-50/40";
                        case "CANCELLED":
                          return "bg-slate-100/50";
                        default:
                          return "bg-white";
                      }
                    };
                    const rowBg = getStatusBg(item.status);

                    return (
                      <React.Fragment key={item.deliveryId}>
                        <DataTableRow
                          id={item.deliveryId}
                          rowSelection={rowSelection}
                          className={cn(
                            "group cursor-pointer hover:bg-slate-50/80 transition-colors border-b-0",
                            selectedIds.has(item.deliveryId)
                              ? "bg-indigo-50/60"
                              : rowBg,
                          )}
                          onClick={() =>
                            navigate({
                              to: "/delivery/$deliveryId",
                              params: { deliveryId: item.deliveryId },
                            })
                          }
                        >
                          <DataTableRowCheckbox
                            id={item.deliveryId}
                            rowSelection={rowSelection}
                          />
                          <TableCell className="apv-col-code w-[195px] overflow-hidden">
                            <div className="flex items-start gap-1.5 mt-0.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 rounded p-0 m-0 text-slate-500 shrink-0"
                                onClick={(e) =>
                                  toggleExpand(item.deliveryId, e)
                                }
                              >
                                {expandedIds.has(item.deliveryId) ? (
                                  <ChevronDown size={14} />
                                ) : (
                                  <ChevronRight size={14} />
                                )}
                              </Button>
                              <div className="flex flex-col items-start gap-1">
                                <span className="font-semibold text-slate-900 whitespace-nowrap leading-tight">
                                  {item.deliveryId}
                                </span>
                                <StatusBadge
                                  type="DeliveryStatus"
                                  value={item.status}
                                  className="text-[9px] px-1.5 py-0 min-h-0 h-4 border-none"
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-semibold text-slate-900 text-xs truncate">
                                {item.customer?.name ||
                                  item.customer?.shortName ||
                                  "—"}
                              </span>
                              <span className="text-[10px] text-slate-500 flex items-center gap-1">
                                <Calendar className="w-2.5 h-2.5" />{" "}
                                {item.deliveryDate
                                  ? new Date(
                                      item.deliveryDate,
                                    ).toLocaleDateString("vi-VN")
                                  : "—"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="w-[210px] overflow-hidden">
                            <div className="flex flex-col min-w-0">
                              <span
                                className="text-xs font-bold text-slate-600 truncate"
                                title={item.driverName || undefined}
                              >
                                {item.driverName || "—"}
                              </span>
                              <span className="text-[10px] text-slate-400 font-mono truncate">
                                {item.vehicleNumber || "—"}
                              </span>
                            </div>
                          </TableCell>

                          <TableCell className="text-right">
                            <div className="text-[11px] font-mono font-semibold text-slate-900">
                              {formatMoney(
                                item.totalAmount ||
                                  item.deliveryItems?.reduce(
                                    (s: number, d: any) =>
                                      s +
                                      (d.deliveredQty || d.quantity || 0) *
                                        (d.unitPrice || d.priceVND || 0),
                                    0,
                                  ) ||
                                  0,
                              )}
                            </div>
                          </TableCell>
                          <TableCell
                            className="text-right apv-col-actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-6 w-6 p-0">
                                  <MoreHorizontal className="h-4 w-4 text-slate-400" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                className="w-[180px] bg-white z-50"
                              >
                                <DropdownMenuLabel className="text-xs">
                                  Giao nhận
                                </DropdownMenuLabel>
                                {canEditDeliveryDocument(item.status) && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      navigate({
                                        to: "/delivery/$deliveryId/edit",
                                        params: { deliveryId: item.deliveryId },
                                      })
                                    }
                                  >
                                    <Edit className="w-4 h-4 mr-2" /> Chỉnh sửa
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => setPrintDelivery(item)}
                                >
                                  <FileText className="w-4 h-4 mr-2" /> In phiếu
                                  xuất (PXK)
                                </DropdownMenuItem>
                                {canUpdateDeliveryLocation(item.status) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      data-testid={`btn-update-location-${item.deliveryId}`}
                                      className="text-indigo-600 focus:text-indigo-700 focus:bg-indigo-50"
                                      onClick={() =>
                                        navigate({
                                          to: "/delivery/$deliveryId/edit",
                                          params: {
                                            deliveryId: item.deliveryId,
                                          },
                                          search: {
                                            focus: "location",
                                          } as any,
                                        })
                                      }
                                    >
                                      <Truck className="w-4 h-4 mr-2" /> Cập
                                      nhật vị trí
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </DataTableRow>
                        {expandedIds.has(item.deliveryId) && (
                          <TableRow
                            className={cn(
                              "border-b-[6px] border-slate-100 last:border-0",
                              selectedIds.has(item.deliveryId)
                                ? "bg-indigo-50/40"
                                : rowBg,
                            )}
                          >
                            <TableCell
                              colSpan={7}
                              className="p-0 pb-2 border-b-0"
                            >
                              <div className="overflow-x-auto">
                                <div className="hidden md:block">
                                  <Table className="w-full text-[11px] text-slate-600 min-w-[700px] table-fixed">
                                    <colgroup>
                                      <col className="apv-col-checkbox" />
                                      <col className="w-[60px]" />
                                      <col className="w-[250px]" />
                                      <col />
                                      <col className="w-[120px]" />
                                      <col className="w-[90px]" />
                                      <col className="w-[90px]" />
                                      <col className="w-[110px]" />
                                      <col className="w-[110px]" />
                                    </colgroup>
                                    <TableHeader className="bg-slate-50 border-b border-slate-200">
                                      <TableRow>
                                        <TableHead
                                          className="apv-col-checkbox w-[40px] px-2 py-2 text-center"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Checkbox
                                            checked={
                                              item.deliveryItems?.length > 0 &&
                                              item.deliveryItems.every(
                                                (d: any, idx: number) =>
                                                  selectedChildIds.has(
                                                    d.id ||
                                                      d.deliveryItemId ||
                                                      String(idx),
                                                  ),
                                              )
                                            }
                                            onCheckedChange={() =>
                                              toggleSelectAllChild(
                                                item.deliveryItems?.map(
                                                  (d: any, idx: number) =>
                                                    d.id ||
                                                    d.deliveryItemId ||
                                                    String(idx),
                                                ) || [],
                                              )
                                            }
                                          />
                                        </TableHead>
                                        <TableHead className="text-center font-semibold text-slate-500">
                                          STT
                                        </TableHead>
                                        <TableHead className="font-semibold text-slate-500">
                                          Mã SP
                                        </TableHead>
                                        <TableHead className="font-semibold text-slate-500">
                                          Tên sản phẩm
                                        </TableHead>
                                        <TableHead className="font-semibold text-slate-500">
                                          Tham chiếu
                                        </TableHead>
                                        <TableHead className="text-right font-semibold text-slate-500">
                                          SL Đặt
                                        </TableHead>
                                        <TableHead className="text-right font-semibold text-slate-500">
                                          SL Giao
                                        </TableHead>
                                        <TableHead className="text-right font-semibold text-slate-500">
                                          SL Thực
                                        </TableHead>
                                        <TableHead className="text-right font-semibold text-slate-500">
                                          Đơn giá
                                        </TableHead>
                                        <TableHead className="text-right font-semibold text-slate-500">
                                          Thành tiền
                                        </TableHead>
                                        <TableHead></TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {!item.deliveryItems ||
                                      item.deliveryItems.length === 0 ? (
                                        <TableRow>
                                          <TableCell
                                            colSpan={11}
                                            className="text-center py-2 text-slate-400"
                                          >
                                            Không có sản phẩm
                                          </TableCell>
                                        </TableRow>
                                      ) : (
                                        item.deliveryItems.map(
                                          (d: any, i: number) => {
                                            const dQty = d.quantity || 0;
                                            const dDelivered =
                                              d.deliveredQty || 0;
                                            const dCounted = d.actualQty || 0;
                                            const dPrice =
                                              d.unitPrice || d.priceVND || 0;
                                            const dAmount =
                                              d.amount || dCounted * dPrice;
                                            return (
                                              <TableRow
                                                key={i}
                                                className="hover:bg-slate-50 border-b border-slate-100/50 last:border-0"
                                              >
                                                <TableCell
                                                  className="apv-col-checkbox px-2 py-2"
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  <Checkbox
                                                    checked={selectedChildIds.has(
                                                      d.id ||
                                                        d.deliveryItemId ||
                                                        String(i),
                                                    )}
                                                    onCheckedChange={() =>
                                                      toggleSelectChild(
                                                        d.id ||
                                                          d.deliveryItemId ||
                                                          String(i),
                                                      )
                                                    }
                                                  />
                                                </TableCell>
                                                <TableCell className="text-center px-2 py-1.5 font-medium text-slate-400">
                                                  {i + 1}
                                                </TableCell>
                                                <TableCell className="px-2 py-1.5 font-mono text-slate-500">
                                                  {d.product?.productCode ||
                                                    d.productCode ||
                                                    "—"}
                                                </TableCell>
                                                <TableCell className="px-2 py-1.5 text-slate-600 text-xs">
                                                  {d.product?.productName ||
                                                    d.productName ||
                                                    "—"}
                                                </TableCell>
                                                <TableCell className="px-2 py-1.5 font-mono text-slate-500 flex flex-col gap-0.5">
                                                  <div className="flex items-center gap-1">
                                                    <span className="text-[9px] text-slate-400 w-4">
                                                      PO
                                                    </span>
                                                    <span className="text-slate-700">
                                                      {d.poNumber || "—"}
                                                    </span>
                                                  </div>
                                                  <div className="flex items-center gap-1">
                                                    <span className="text-[9px] text-slate-400 w-4">
                                                      SO
                                                    </span>
                                                    <span className="text-slate-700">
                                                      {d.soNumber || "—"}
                                                    </span>
                                                  </div>
                                                </TableCell>
                                                <TableCell className="text-right px-2 py-1.5 font-mono">
                                                  {dQty.toLocaleString()}
                                                </TableCell>
                                                <TableCell className="text-right px-2 py-1.5 font-mono font-medium text-indigo-600">
                                                  {dDelivered.toLocaleString()}
                                                </TableCell>
                                                <TableCell className="text-right px-2 py-1.5 font-mono font-bold text-emerald-700">
                                                  {dCounted.toLocaleString()}
                                                </TableCell>
                                                <TableCell className="text-right px-2 py-1.5 font-mono">
                                                  {dPrice.toLocaleString()}
                                                </TableCell>
                                                <TableCell className="text-right px-2 py-1.5 font-mono font-bold text-slate-700">
                                                  {dAmount.toLocaleString()}
                                                </TableCell>
                                                <TableCell></TableCell>
                                              </TableRow>
                                            );
                                          },
                                        )
                                      )}
                                    </TableBody>
                                  </Table>
                                </div>
                                {/* Mobile view */}
                                <div className="md:hidden flex flex-col gap-2 p-2 bg-slate-50">
                                  {!item.deliveryItems ||
                                  item.deliveryItems.length === 0 ? (
                                    <div className="text-center py-4 text-xs text-slate-400 bg-white rounded-lg border border-slate-100">
                                      Không có sản phẩm
                                    </div>
                                  ) : (
                                    item.deliveryItems.map(
                                      (d: any, i: number) => {
                                        const dQty = d.quantity || 0;
                                        const dDelivered = d.deliveredQty || 0;
                                        return (
                                          <div
                                            key={i}
                                            className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm flex flex-col gap-2"
                                          >
                                            <div className="flex justify-between items-start gap-2">
                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <span className="text-[10px] font-bold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                                                    #{i + 1}
                                                  </span>
                                                  <span className="text-xs font-mono text-slate-500">
                                                    {d.product?.productCode ||
                                                      d.productCode ||
                                                      "—"}
                                                  </span>
                                                </div>
                                                <div className="text-xs text-slate-700 leading-tight">
                                                  {d.product?.productName ||
                                                    d.productName ||
                                                    "—"}
                                                </div>
                                              </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-1 pt-2 border-t border-slate-100 text-xs">
                                              <div className="flex flex-col">
                                                <span className="text-[10px] text-slate-400">
                                                  PO Number
                                                </span>
                                                <span className="font-mono text-slate-700">
                                                  {d.poNumber || "—"}
                                                </span>
                                              </div>
                                              <div className="flex flex-col">
                                                <span className="text-[10px] text-slate-400">
                                                  SO Number
                                                </span>
                                                <span className="font-mono text-slate-700">
                                                  {d.soNumber || "—"}
                                                </span>
                                              </div>
                                              <div className="flex flex-col">
                                                <span className="text-[10px] text-slate-400">
                                                  SL Đặt
                                                </span>
                                                <span className="font-mono font-medium text-slate-700">
                                                  {dQty.toLocaleString()}
                                                </span>
                                              </div>
                                              <div className="flex flex-col">
                                                <span className="text-[10px] text-slate-400">
                                                  SL Giao
                                                </span>
                                                <span className="font-mono font-bold text-indigo-600">
                                                  {dDelivered.toLocaleString()}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      },
                                    )
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  }}
                />
              </TableBody>
            </Table>
          </div>
        </ResponsiveDataView>
        )}
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />

      <ImportDialog
        title="Nhập phiếu giao hàng"
        open={showImport}
        onOpenChange={setShowImport}
        columns={EXPORT_COLUMNS}
        onConfirmImport={handleImport}
        onDownloadTemplate={() =>
          downloadTemplate(EXPORT_COLUMNS, "phieu_giao_hang")
        }
      />

      <PrintDialog
        open={!!printDelivery}
        onClose={() => setPrintDelivery(null)}
        type="delivery"
        data={printDelivery}
      />

      <FQCReadySelectModal
        open={showFqcModal}
        onOpenChange={setShowFqcModal}
      />
    </>
  );
}
